import { useFormik } from "formik";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { TAB_NAMES } from "src/apps/common/menu-navigation/menuNavigation";
import TabHeader from "src/apps/common/tab-header/TabHeader";
import { useGetRoles } from "src/apps/super-admin/tabs/roles/api-clients";
import { RoleType } from "src/apps/super-admin/tabs/roles/list/role-types";
import Button from "src/components/buttons/Button";
import DatePicker from "src/components/calendar/DatePicker";
import Dropdown from "src/components/dropdowns/Dropdown";
import UploadImage from "src/components/image-upload/UploadImage";
import Input from "src/components/inputs/Input";
import ConfirmationModal from "src/components/modals/ConfirmationModal";
import ToggleWithLabel from "src/components/toggles/Toggle";
import useNotification from "src/hooks/useNotification";
import useSetActiveTab from "src/hooks/useSetActiveTab";
import { IDENTITY_DOCUMENTS_OPTIONS } from "src/utils/constants";
import { number, object, string } from "yup";
import {
  useCreateStaff,
  useDeleteStaffIdentityDoc,
  useDeleteStaffImage,
  useGetStaffData,
  useUpdateStaff,
} from "../api-clients";

const FORM_FIELDS = {
  NAME: "name",
  EMAIL: "email",
  PASSWORD: "password",
  PHONE: "phone",
  EMERGENCY_PHONE: "emergencyPhone",
  BASE_SALARY: "salary",
  DOJ: "doj",
  ROLE: "role",
  COMMISSION: "commission",
  IDENTITY_DOCUMENT: "identityDoc",
  IDENTITY_NUMBER: "identityNumber",
  IDENTITY_DOCUMENT_IMAGE: "identityDocImage",
  USER_IMAGE: "userImage",
  STATUS: "active",
  ADDRESS: "address",
};

const CreateStaff = ({ isInViewMode = false }: { isInViewMode?: boolean }) => {
  useSetActiveTab(TAB_NAMES.STAFF_MANAGEMENT);
  const navigate = useNavigate();
  const { staffId: id } = useParams();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteStaffImageModalOpen, setIsDeleteStaffImageModalOpen] =
    useState(false);

  const staffId = useMemo(() => Number(id), [id]);

  const isInEditMode = useMemo(() => !!staffId, [staffId]);

  const { errorNotification, successNotification } = useNotification();

  const { isLoading: isGetStaffLoading, data } = useGetStaffData(staffId);
  const {
    isLoading: isDeleteLoading,
    execute: deleteIdentityDoc,
    isSuccess: isDeleteIdentityDocSuccess,
    error: isDeleteIdentityDocFailed,
  } = useDeleteStaffIdentityDoc();

  const {
    isLoading: isDeleteStaffImageLoading,
    execute: deleteStaffImage,
    isSuccess: isDeleteStaffImageSuccess,
    error: isDeleteStaffImageFailed,
  } = useDeleteStaffImage();

  const staffData = useMemo(() => data?.data ?? {}, [data?.data]);

  const {
    isLoading: isUpdateLoading,
    execute: updateStaff,
    isSuccess: isUpdateSuccess,
    error: isUpdateError,
  } = useUpdateStaff(staffId);

  const {
    isLoading: isRolesLoading,
    error: rolesError,
    data: rolesData,
    mutate: updateRoleData,
    isValidating,
  } = useGetRoles({});

  const roles = useMemo(
    () =>
      (rolesData?.data?.rows ?? []).map((role: RoleType) => ({
        label: role.name,
        value: role.id,
      })),
    [rolesData?.data?.rows]
  );

  const { isLoading, isSuccess, execute, error } = useCreateStaff();

  const { values, errors, handleSubmit, setFieldValue, touched } = useFormik({
    enableReinitialize: true,
    initialValues: {
      [FORM_FIELDS.NAME]: staffData?.[FORM_FIELDS.NAME] ?? null,
      [FORM_FIELDS.EMAIL]: staffData?.[FORM_FIELDS.EMAIL] ?? "",
      [FORM_FIELDS.PHONE]: staffData?.[FORM_FIELDS.PHONE] ?? "",
      [FORM_FIELDS.EMERGENCY_PHONE]:
        staffData?.[FORM_FIELDS.EMERGENCY_PHONE] ?? "",
      [FORM_FIELDS.ADDRESS]: staffData?.[FORM_FIELDS.ADDRESS] ?? "",
      [FORM_FIELDS.BASE_SALARY]: staffData?.[FORM_FIELDS.BASE_SALARY] ?? null,
      [FORM_FIELDS.DOJ]: staffData?.[FORM_FIELDS.DOJ] ?? null,
      [FORM_FIELDS.ROLE]:
        staffData?.storeAccess?.[0]?.[FORM_FIELDS.ROLE] ?? null,
      [FORM_FIELDS.COMMISSION]: staffData?.[FORM_FIELDS.COMMISSION] ?? null,
      [FORM_FIELDS.IDENTITY_DOCUMENT]:
        Number(staffData?.[FORM_FIELDS.IDENTITY_DOCUMENT]) ?? "",
      [FORM_FIELDS.IDENTITY_NUMBER]:
        staffData?.[FORM_FIELDS.IDENTITY_NUMBER] ?? "",
      [FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE]:
        staffData?.[FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE] ?? "",
      [FORM_FIELDS.USER_IMAGE]: staffData?.[FORM_FIELDS.USER_IMAGE] ?? "",
      [FORM_FIELDS.STATUS]:
        staffData?.storeAccess?.[0]?.[FORM_FIELDS.STATUS] ?? false,
    },
    validationSchema: object({
      [FORM_FIELDS.NAME]: string().required("This is a Required field."),
      [FORM_FIELDS.PHONE]: number().required("This is a Required field."),
      [FORM_FIELDS.EMAIL]: string()
        .email("Please enter valid email.")
        .required("This is a Required field."),
      [FORM_FIELDS.EMERGENCY_PHONE]: number().required(
        "This is a Required field."
      ),
      [FORM_FIELDS.ROLE]: number().required("This is a Required field."),
      // [FORM_FIELDS.PASSWORD]: string().required("This is a Required field."),
    }),
    onSubmit: (data) => {
      if (isInEditMode) {
        updateStaff(data);
        return;
      }
      execute(data);
    },
  });

  useEffect(() => {
    if (isSuccess || isUpdateSuccess) {
      successNotification(isUpdateSuccess ? "Staff Updated" : "Staff Created.");
      navigate("/store/staff");
    }
  }, [isSuccess, isUpdateSuccess, navigate, successNotification]);

  useEffect(() => {
    if (isDeleteIdentityDocSuccess) {
      setFieldValue(FORM_FIELDS.IDENTITY_DOCUMENT, null);
      successNotification("IdentityDoc deleted");
      setIsDeleteModalOpen(false);
    }
  }, [isDeleteIdentityDocSuccess, setFieldValue, successNotification]);

  useEffect(() => {
    if (isDeleteStaffImageSuccess) {
      setFieldValue(FORM_FIELDS.USER_IMAGE, null);
      successNotification("Staff Image deleted");
      setIsDeleteStaffImageModalOpen(false);
    }
  }, [isDeleteStaffImageSuccess, setFieldValue, successNotification]);

  useEffect(() => {
    if (
      error ||
      isUpdateError ||
      isDeleteIdentityDocFailed ||
      isDeleteStaffImageFailed
    ) {
      errorNotification();
    }
  }, [
    error,
    errorNotification,
    isUpdateError,
    isDeleteIdentityDocFailed,
    isDeleteStaffImageFailed,
  ]);

  const isInLoading = useMemo(
    () => isLoading || isGetStaffLoading || isUpdateLoading,
    [isGetStaffLoading, isLoading, isUpdateLoading]
  );

  const isFieldDisabled = useMemo(
    () => isInViewMode || isInLoading,
    [isInLoading, isInViewMode]
  );

  const getFieldError = useCallback(
    (fieldName: string) => {
      return touched[fieldName] ? (errors[fieldName] as string) : undefined;
    },
    [errors, touched]
  );

  return (
    <>
      {" "}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        isLoading={isDeleteLoading}
        onSubmit={() => deleteIdentityDoc(staffId)}
      />
      <ConfirmationModal
        isOpen={isDeleteStaffImageModalOpen}
        onClose={() => setIsDeleteStaffImageModalOpen(false)}
        isLoading={isDeleteStaffImageLoading}
        onSubmit={() => deleteStaffImage(staffId)}
      />
      <div className="flex flex-col gap-[20px] h-full">
        <TabHeader label="Add Staff" showBreadcrumb />
        <div className="flex flex-col justify-between gap-6 bg-white rounded-md py-[30px] px-[42px] h-fit">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-[30px]">
            <Input
              label="Employee Name"
              placeholder="Full Name"
              name={FORM_FIELDS.NAME}
              value={values[FORM_FIELDS.NAME] as string}
              error={getFieldError(FORM_FIELDS.NAME)}
              onChange={(e) => setFieldValue(FORM_FIELDS.NAME, e.target.value)}
              required
              disabled={isFieldDisabled}
            />
            <Input
              type="email"
              label="Email"
              placeholder="Email"
              name={FORM_FIELDS.EMAIL}
              value={values[FORM_FIELDS.EMAIL] as string}
              error={getFieldError(FORM_FIELDS.EMAIL)}
              onChange={(e) => setFieldValue(FORM_FIELDS.EMAIL, e.target.value)}
              disabled={isFieldDisabled}
              required
            />
            <Input
              type="number"
              label="Phone Number"
              placeholder="Phone Number"
              name={FORM_FIELDS.PHONE}
              value={values[FORM_FIELDS.PHONE] as string}
              error={getFieldError(FORM_FIELDS.PHONE)}
              onChange={(e) => setFieldValue(FORM_FIELDS.PHONE, e.target.value)}
              disabled={isFieldDisabled}
              required
            />

            {/* <Input
              type="password"
              label="Password"
              placeholder="Password"
              name={FORM_FIELDS.PASSWORD}
              value={values[FORM_FIELDS.PASSWORD] as string}
              error={
                touched[FORM_FIELDS.PASSWORD]
                  ? errors[FORM_FIELDS.PASSWORD]
                  : undefined
              }
              onChange={(e) =>
                setFieldValue(FORM_FIELDS.PASSWORD, e.target.value)
              }
              disabled={isFieldDisabled}
              required
            /> */}
            <Input
              type="number"
              label="Emergency Contact"
              placeholder="Emergency Contact"
              name={FORM_FIELDS.EMERGENCY_PHONE}
              value={values[FORM_FIELDS.EMERGENCY_PHONE] as string}
              error={getFieldError(FORM_FIELDS.EMERGENCY_PHONE)}
              onChange={(e) =>
                setFieldValue(FORM_FIELDS.EMERGENCY_PHONE, e.target.value)
              }
              disabled={isFieldDisabled}
              required
            />

            <Input
              label="Address"
              placeholder="Address"
              name={FORM_FIELDS.ADDRESS}
              value={values[FORM_FIELDS.ADDRESS] as string}
              error={getFieldError(FORM_FIELDS.ADDRESS)}
              onChange={(e) =>
                setFieldValue(FORM_FIELDS.ADDRESS, e.target.value)
              }
              disabled={isFieldDisabled}
            />

            <DatePicker
              label="Date Of Joining (MM/DD/YY)"
              value={values[FORM_FIELDS.DOJ]}
              onChange={(value) => setFieldValue(FORM_FIELDS.DOJ, value)}
              disabled={isFieldDisabled}
            />
            <Dropdown
              label="Role"
              placeholder="Select Employee Role"
              className={"w-full"}
              options={roles}
              value={values[FORM_FIELDS.ROLE] as string}
              onChange={(val) => setFieldValue(FORM_FIELDS.ROLE, val)}
              disabled={isFieldDisabled}
              isLoading={isRolesLoading}
              required
              error={getFieldError(FORM_FIELDS.ROLE)}
            />
            <Input
              type="number"
              label="Basic salary"
              placeholder="Basic salary"
              name={FORM_FIELDS.BASE_SALARY}
              value={values[FORM_FIELDS.BASE_SALARY] as string}
              error={getFieldError(FORM_FIELDS.BASE_SALARY)}
              onChange={(e) =>
                setFieldValue(FORM_FIELDS.BASE_SALARY, e.target.value)
              }
              disabled={isFieldDisabled}
            />
            <Input
              type="number"
              label="Commission"
              placeholder="Commission"
              name={FORM_FIELDS.COMMISSION}
              value={values[FORM_FIELDS.COMMISSION] as string}
              error={getFieldError(FORM_FIELDS.COMMISSION)}
              onChange={(e) =>
                setFieldValue(FORM_FIELDS.COMMISSION, e.target.value)
              }
              disabled={isFieldDisabled}
              rightIcon={() => (
                <div className="h-full border-l-[1px] flex justify-center items-center p-2 absolute right-0 text-gray-suva text-14px">
                  %
                </div>
              )}
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Dropdown
                label="Identity Document Name"
                placeholder="Select Document Type"
                className={"!w-full"}
                options={IDENTITY_DOCUMENTS_OPTIONS}
                value={values[FORM_FIELDS.IDENTITY_DOCUMENT] as string}
                onChange={(val) =>
                  setFieldValue(FORM_FIELDS.IDENTITY_DOCUMENT, val)
                }
                disabled={isFieldDisabled}
              />
              <Input
                label="Identity Number"
                placeholder="Identity Number"
                name={FORM_FIELDS.IDENTITY_NUMBER}
                value={values[FORM_FIELDS.IDENTITY_NUMBER] as string}
                error={getFieldError(FORM_FIELDS.IDENTITY_NUMBER)}
                onChange={(e) =>
                  setFieldValue(FORM_FIELDS.IDENTITY_NUMBER, e.target.value)
                }
                disabled={isFieldDisabled}
                className="w-full"
              />
            </div>
            <UploadImage
              name={FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE}
              label="Identity Document Upload"
              value={values[FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE]}
              onChange={(file) =>
                setFieldValue(FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE, file)
              }
              error={getFieldError(FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE)}
              onClear={() => {
                if (
                  typeof values[FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE] ===
                  "string"
                ) {
                  setIsDeleteModalOpen(true);
                  return;
                }
                setFieldValue(FORM_FIELDS.IDENTITY_DOCUMENT_IMAGE, null);
              }}
            />
            <UploadImage
              name={FORM_FIELDS.USER_IMAGE}
              label="User Image Upload"
              value={values[FORM_FIELDS.USER_IMAGE]}
              onChange={(file) => setFieldValue(FORM_FIELDS.USER_IMAGE, file)}
              error={getFieldError(FORM_FIELDS.USER_IMAGE)}
              onClear={() => {
                if (typeof values[FORM_FIELDS.USER_IMAGE] === "string") {
                  setIsDeleteStaffImageModalOpen(true);
                  return;
                }
                setFieldValue(FORM_FIELDS.USER_IMAGE, null);
              }}
            />
            <ToggleWithLabel
              label="Status"
              checked={values[FORM_FIELDS.STATUS] as boolean}
              onToggle={(val) => [setFieldValue(FORM_FIELDS.STATUS, val)]}
              disabled={isFieldDisabled}
              showToggleStatus
            />
          </div>
          <Button
            text={isLoading ? "Loading..." : "Submit"}
            onClick={handleSubmit}
            primary
            className="h-[40px] px-10"
            isDisabled={isFieldDisabled}
          />
        </div>
      </div>
    </>
  );
};

export default CreateStaff;
